import type { OccasionId } from '../occasion/occasion';
import type { IdentityImageConfig } from '../identity/identityImage';

export type EventId = string;

export const EventStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  LIVE: 'live',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
  CANCELLED: 'cancelled',
} as const;
export type EventStatus = (typeof EventStatus)[keyof typeof EventStatus];

export const EventVisibility = {
  PRIVATE: 'private',
  GUEST_VISIBLE: 'guest_visible',
  PUBLIC: 'public',
} as const;
export type EventVisibility =
  (typeof EventVisibility)[keyof typeof EventVisibility];

export const EventType = {
  CEREMONY: 'ceremony',
  RECEPTION: 'reception',
  MEAL: 'meal',
  TRAVEL: 'travel',
  LODGING: 'lodging',
  MEETING: 'meeting',
  OTHER: 'other',
} as const;
export type EventType = (typeof EventType)[keyof typeof EventType];

export interface EventSummary {
  id: EventId;
  occasionId: OccasionId;
  eventKey: string;
  displayName: string;
  identityImage: IdentityImageConfig;
  identityImageUrl?: string | null;
  eventType: EventType;
  description: string | null;
  guestSummary: string | null;
  status: EventStatus;
  visibility: EventVisibility;
  resolvedLocaleCode?: string | null;
  startsAt: string | null;
  endsAt: string | null;
  timezone: string | null;
  locationName: string | null;
  locationAddress: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EventCreateInput {
  eventKey?: string;
  displayName: string;
  identityImage?: IdentityImageConfig;
  eventType: EventType;
  description: string | null;
  guestSummary: string | null;
  status: EventStatus;
  visibility: EventVisibility;
  startsAt: string | null;
  endsAt: string | null;
  timezone: string | null;
  locationName: string | null;
  locationAddress: string | null;
  sortOrder: number | null;
}

export interface EventUpdateInput {
  eventKey?: string;
  displayName?: string;
  identityImage?: IdentityImageConfig;
  eventType?: EventType;
  description?: string | null;
  guestSummary?: string | null;
  status?: EventStatus;
  visibility?: EventVisibility;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  locationName?: string | null;
  locationAddress?: string | null;
  sortOrder?: number | null;
}

function eventTimestampValue(value: string | null): number {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }

  return new Date(value).getTime();
}

export function compareEventDisplayOrder(
  left: EventSummary,
  right: EventSummary,
): number {
  if (left.sortOrder !== right.sortOrder) {
    return left.sortOrder - right.sortOrder;
  }

  const startDifference =
    eventTimestampValue(left.startsAt) - eventTimestampValue(right.startsAt);

  if (startDifference !== 0) {
    return startDifference;
  }

  const nameDifference = left.displayName.localeCompare(right.displayName);

  if (nameDifference !== 0) {
    return nameDifference;
  }

  return left.createdAt.localeCompare(right.createdAt);
}
